// Warehouse IP allowlist configuration.
//
// This is a *soft* gate — a client-side check that blocks the UI when the
// visitor's public IP isn't in the allowlist. It is NOT real security.
// Anyone who knows the backend URL and has valid credentials can bypass it
// with curl. For real network security, the backend team also needs to add
// an IP allowlist middleware (see docs/BACKEND_IP_ALLOWLIST_SPEC.md).
//
// -----------------------------------------------------------------------------
// TO CONFIGURE:
//   1. Open https://ifconfig.me/ from a warehouse PC and note the IP.
//   2. Replace the string(s) in `WAREHOUSE_ALLOWLIST` below with your IP(s).
//   3. If you have multiple locations, add them all.
//   4. To temporarily disable the gate (e.g. during a network migration),
//      set `ALLOWLIST_ENABLED = false`.
// -----------------------------------------------------------------------------

/** Toggle the whole gate on/off. Flip to `false` to allow all visitors. */
export const ALLOWLIST_ENABLED = true;

/**
 * Allowed public IPv4 and IPv6 addresses. Exact match against the IP reported
 * by the IP lookup service. Prefix matches ("10.0.0." → any address starting
 * with that) are also supported.
 */
export const WAREHOUSE_ALLOWLIST = [
  "96.89.251.90",   // Cabinet Depot warehouse — primary public IP
];

/**
 * A shared "field manager" bypass code. When someone appends
 * `?bypass=<code>` to the URL, they're allowed through and the exemption is
 * persisted in localStorage for 30 days. Change this if it ever leaks.
 * Client-side friction reducer only — not a security boundary.
 */
export const BYPASS_CODES = ["cd-warehouse-2026"];

const BYPASS_STORAGE_KEY = "cd_ip_bypass_until";
const BYPASS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/** True when a matching bypass code was used within the last 30 days. */
export function hasActiveBypass() {
  try {
    const until = Number(localStorage.getItem(BYPASS_STORAGE_KEY));
    if (!until || Number.isNaN(until)) return false;
    if (Date.now() > until) {
      localStorage.removeItem(BYPASS_STORAGE_KEY);
      return false;
    }
    return true;
  } catch (err) {
    return false;
  }
}

/** Persist a bypass exemption for 30 days. */
export function grantBypass() {
  try { localStorage.setItem(BYPASS_STORAGE_KEY, String(Date.now() + BYPASS_TTL_MS)); }
  catch (err) { /* noop */ }
}

/** Clear an active bypass (used by the Settings "return to warehouse-only" button). */
export function revokeBypass() {
  try { localStorage.removeItem(BYPASS_STORAGE_KEY); }
  catch (err) { /* noop */ }
}

/** Check whether a given IP string is in the allowlist. */
export function isIpAllowed(ip) {
  if (!ip) return false;
  for (const entry of WAREHOUSE_ALLOWLIST) {
    if (!entry) continue;
    const e = String(entry).trim();
    if (!e) continue;
    if (e.endsWith(".") || e.endsWith(":")) {
      // Prefix match — useful for subnets like "10.0.0."
      if (ip.startsWith(e)) return true;
    } else if (ip === e) {
      return true;
    }
  }
  return false;
}

/** Fetch the visitor's public IP. Uses ipify (free, no API key). */
export async function fetchPublicIp({ signal } = {}) {
  const res = await fetch("https://api.ipify.org?format=json", { signal });
  if (!res.ok) throw new Error(`IP lookup failed: HTTP ${res.status}`);
  const data = await res.json();
  return data?.ip || null;
}

/**
 * Read `?bypass=<code>` from the current URL, validate it against BYPASS_CODES,
 * and (if valid) persist the exemption + strip the query param so it doesn't
 * linger in the address bar or get shared accidentally.
 */
export function consumeBypassFromUrl() {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("bypass");
    if (!code) return false;
    if (!BYPASS_CODES.includes(code)) return false;
    grantBypass();
    params.delete("bypass");
    const rest = params.toString();
    const newUrl = window.location.pathname + (rest ? `?${rest}` : "") + window.location.hash;
    window.history.replaceState({}, "", newUrl);
    return true;
  } catch (err) {
    return false;
  }
}
